/**
 * Projéteis de INIMIGOS -> Player (Fase 8: Aether Bolt do Aetherwing / Shard Burst do Warden).
 * Pool, geometria/material compartilhados. Respeita a BARREIRA da Sanctuary (a bolt some na
 * borda, sem dano) e a imunidade do Player (DamageSystem lê invulnerable). Mobile-first.
 */
import * as THREE from "three";
import { Pool } from "../utils/Pool";
import { dealDamage } from "./DamageSystem";
import { events } from "../core/Events";
import type { Damageable } from "./Damageable";
import type { Sanctuary } from "../world/Sanctuary";
import { wallBetween } from "../world/TerritoryGates";

interface Bolt {
  mesh: THREE.Mesh;
  dir: THREE.Vector3;
  speed: number;
  life: number;
  damage: number;
  alive: boolean;
}

const _prev = new THREE.Vector3();
const _impact = new THREE.Vector3();

export class EnemyProjectiles {
  private readonly active: Bolt[] = [];
  private readonly pool: Pool<Bolt>;
  private readonly geo = new THREE.IcosahedronGeometry(0.22, 0);
  private readonly mat = new THREE.MeshBasicMaterial({ color: 0x8fe8ff });
  private sanctuary: Sanctuary | null = null;

  constructor(private readonly scene: THREE.Scene) {
    this.pool = new Pool<Bolt>(
      () => this.make(),
      (b) => {
        b.mesh.visible = false;
        b.alive = false;
      },
      6,
    );
  }

  setSanctuary(s: Sanctuary): void {
    this.sanctuary = s;
  }

  fire(origin: THREE.Vector3, dirX: number, dirZ: number, speed: number, damage: number, color: number): void {
    const b = this.pool.acquire();
    b.mesh.position.set(origin.x, Math.max(0.8, origin.y), origin.z);
    b.dir.set(dirX, 0, dirZ);
    if (b.dir.lengthSq() < 1e-6) b.dir.set(0, 0, 1);
    b.dir.normalize();
    b.speed = speed;
    b.life = 4;
    b.damage = damage;
    b.alive = true;
    (b.mesh.material as THREE.MeshBasicMaterial).color.setHex(color);
    b.mesh.visible = true;
    this.active.push(b);
  }

  update(dt: number, player: Damageable): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const b = this.active[i];
      b.life -= dt;
      _prev.copy(b.mesh.position);
      b.mesh.position.addScaledVector(b.dir, b.speed * dt);
      b.mesh.rotation.y += dt * 6;

      // 10.6O: bolt que cruzou uma parede é destruído
      if (wallBetween(_prev.x, _prev.z, b.mesh.position.x, b.mesh.position.z)) {
        this.release(i);
        continue;
      }

      // barreira da Sanctuary: some na borda sem dano
      if (this.sanctuary && this.sanctuary.projectileCrossing(_prev, b.mesh.position, _impact)) {
        events.emit("sanctuary:projectileBlocked", { position: _impact.clone() });
        this.release(i);
        continue;
      }

      // acerto no player
      if (!player.isDead) {
        const pr = (player as { radius?: number }).radius ?? 0.5;
        const dx = player.position.x - b.mesh.position.x;
        const dz = player.position.z - b.mesh.position.z;
        const reach = 0.5 + pr; // 10.9AI: 0.35→0.5 — bolt "de raspão" conecta (menos erro por jitter)
        if (dx * dx + dz * dz <= reach * reach) {
          dealDamage(player, { baseDamage: b.damage, critChance: 0, critDamageMult: 1, armor: 0 }, { targetKind: "player", damageType: "piercing", knockbackFrom: b.mesh.position, knockbackForce: 2 });
          this.release(i);
          continue;
        }
      }
      if (b.life <= 0) this.release(i);
    }
  }

  private release(i: number): void {
    this.pool.release(this.active[i]);
    this.active.splice(i, 1);
  }

  clear(): void {
    for (const b of this.active) this.pool.release(b);
    this.active.length = 0;
  }

  private make(): Bolt {
    const mesh = new THREE.Mesh(this.geo, this.mat.clone());
    mesh.frustumCulled = false;
    mesh.visible = false;
    this.scene.add(mesh);
    return { mesh, dir: new THREE.Vector3(0, 0, 1), speed: 0, life: 0, damage: 0, alive: false };
  }
}
